import { createContext, useContext, useEffect, useState } from 'react';
import { categories } from '../services/londi-constants';
import { getFundingMatching } from '../services/londi-funding-matching';
import { getTestFields } from '../services/londi-suggestions';
import { screeningResultsState } from './state';
import { DEFAULT_RECOMMENDATIONS_FILTER } from '../components/promotion/FundingRecomendationsFilter';

export const initializer = (initialValue = screeningResultsState) => JSON.parse(sessionStorage.getItem('kidInfo')) || initialValue;

export const DiagnosticContext = createContext();

export const useOutsideClick = (ref) => {
  const { dispatch } = useContext(DiagnosticContext);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        dispatch({ type: 'options' });
      }
    };
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dispatch, ref]);
};

export const showFundingRecommendations = (state, filterQuery = DEFAULT_RECOMMENDATIONS_FILTER) => {
  const resultsReading = getTestFields(state.readingTestId, categories.Reading).map((el, i) => {
    return { ...el, result: state?.resultsReading?.[i] };
  });

  const secondResultsReading = getTestFields(state.readingSecondTestId, categories.Reading).map((el, i) => {
    return { ...el, result: state?.resultsSecondReading?.[i] };
  });

  const thirdResultsReading = getTestFields(state.readingThirdTestId, categories.Reading).map((el, i) => {
    return { ...el, result: state?.resultsThirdReading?.[i] };
  });

  const resultsWriting = getTestFields(state.writingTestId, categories.Writing).map((el, i) => {
    return { ...el, result: state?.resultsWriting?.[i] };
  });

  const secondResultsWriting = getTestFields(state.writingSecondTestId, categories.Writing).map((el, i) => {
    return { ...el, result: state?.resultsSecondWriting?.[i] };
  });

  const thirdResultsWriting = getTestFields(state.writingThirdTestId, categories.Writing).map((el, i) => {
    return { ...el, result: state?.resultsThirdWriting?.[i] };
  });

  const resultsCounting = getTestFields(state.countingTestId, categories.Math).map((el, i) => {
    return { ...el, result: state?.resultsCounting?.[i] };
  });

  const secondResultsCounting = getTestFields(state.countingSecondTestId, categories.Math).map((el, i) => {
    return { ...el, result: state?.resultsSecondCounting?.[i] };
  });

  const thirdResultsCounting = getTestFields(state.countingThirdTestId, categories.Math).map((el, i) => {
    return { ...el, result: state?.resultsThirdCounting?.[i] };
  });

  // Helper function to create self-assessment results matching stable version format
  const createSelfAssessmentResults = (categoryId, k1, k2, k3, k4) => {
    // Check if any self-assessment results exist for this category
    const hasAnyResults = [k1, k2, k3, k4].some(k => 
      k?.result !== undefined && k?.result !== null && k?.result !== ''
    );
    
    // If no self-assessment results exist, return empty array
    if (!hasAnyResults) {
      return [];
    }
    
    // Otherwise, return the results (with potential null/undefined values)
    return [
      {result: k1?.result , competenceLevel: 1, category: categoryId},
      {result: k2?.result , competenceLevel: 2, category: categoryId},
      {result: k3?.result , competenceLevel: 3, category: categoryId},
      {result: k4?.result , competenceLevel: 4, category: categoryId},
    ];
  };

  const selfAssessmentReadingResults = createSelfAssessmentResults(
    categories.Reading,
    state?.readingK1Results,
    state?.readingK2Results,
    state?.readingK3Results,
    state?.readingK4Results
  );

  const selfAssessmentWritingResults = createSelfAssessmentResults(
    categories.Writing,
    state?.writingK1Results,
    state?.writingK2Results,
    state?.writingK3Results,
    state?.writingK4Results
  );

  const selfAssessmentCountingResults = createSelfAssessmentResults(
    categories.Math,
    state?.countingK1Results,
    state?.countingK2Results,
    state?.countingK3Results,
    state?.countingK4Results
  );

  // Helper function to merge test results with self-assessment as fallback
  const mergeResultsWithSelfAssessment = (testResults, selfAssessmentResults, categoryId) => {
    if (!testResults || testResults.length === 0) {
      return selfAssessmentResults;
    }

    // Get self-assessment domain selection and answers from sessionStorage
    const selfAssessmentDomainChecked = JSON.parse(sessionStorage.getItem('saDomainChecked')) || {};
    const selfAssessmentAnswers = JSON.parse(sessionStorage.getItem('saAnswers')) || {};
    const selfAssessmentLevelVisible = JSON.parse(sessionStorage.getItem('saLevelVisible')) || {};
    
    // Check if this domain was actually selected in self-assessment
    const domainWasSelectedInSelfAssessment = () => {
      if (categoryId === categories.Reading) {
        return selfAssessmentDomainChecked.Reading === true;
      } else if (categoryId === categories.Writing) {
        return selfAssessmentDomainChecked.Writing === true;
      } else if (categoryId === categories.Math) {
        return selfAssessmentDomainChecked.Counting === true;
      }
      return false;
    };

    // Check if all questions for a specific competence level were answered in self-assessment
    const allQuestionsAnsweredForLevel = (competenceLevel) => {
      if (!domainWasSelectedInSelfAssessment()) return false;
      
      const domainKey = categoryId === categories.Reading ? 'Reading' : 
                       categoryId === categories.Writing ? 'Writing' : 
                       categoryId === categories.Math ? 'Counting' : null;
      
      if (!domainKey) return false;
      
      const levelKey = `k${competenceLevel}`;
      
      // Check if this level was visible/enabled in self-assessment
      const levelWasVisible = selfAssessmentLevelVisible[domainKey]?.[levelKey];
      if (!levelWasVisible) return false;
      
      // Check if all questions for this level were answered (no null values)
      const levelAnswers = selfAssessmentAnswers[domainKey]?.[levelKey] || [];
      return levelAnswers.length > 0 && levelAnswers.every(answer => answer !== null && answer !== undefined);
    };

    // Priority: test results first, self-assessment to fill gaps ONLY if ALL questions for that KS were answered
    return testResults.map((testResult) => {
      // If test result has actual data, use it
      if (testResult.result !== undefined && testResult.result !== null && testResult.result !== '') {
        return testResult;
      }

      // Only use self-assessment as fallback if:
      // 1. The domain was selected in self-assessment AND
      // 2. ALL questions for this specific competence level were answered AND
      // 3. There are self-assessment results available for this level
      if (allQuestionsAnsweredForLevel(testResult.competenceLevel)) {
        const selfAssessment = selfAssessmentResults.find((sa) => sa.competenceLevel === testResult.competenceLevel);
        if (selfAssessment && selfAssessment.result !== undefined && selfAssessment.result !== null) {
          return { ...testResult, result: selfAssessment.result };
        }
      }

      // No data available or incomplete self-assessment - keep test result even if null
      return testResult;
    });
  };

  // Determine final results using proper priority logic
  const finalReadingResults = state.readingTestId
    ? mergeResultsWithSelfAssessment(resultsReading, selfAssessmentReadingResults, categories.Reading)
    : selfAssessmentReadingResults;

  const finalWritingResults = state.writingTestId
    ? mergeResultsWithSelfAssessment(resultsWriting, selfAssessmentWritingResults, categories.Writing)
    : selfAssessmentWritingResults;

  const finalCountingResults = state.countingTestId
    ? mergeResultsWithSelfAssessment(resultsCounting, selfAssessmentCountingResults, categories.Math)
    : selfAssessmentCountingResults;

  const fundingMatchingRecomendations = {
    education: state.education,
    screeningResults: [
      {
        category: categories.Reading,
        testId: state.readingTestId,
        results: finalReadingResults,
      },
      {
        category: categories.Reading,
        testId: state.readingSecondTestId,
        results: secondResultsReading,
      },
      {
        category: categories.Reading,
        testId: state.readingThirdTestId,
        results: thirdResultsReading,
      },
      {
        category: categories.Writing,
        testId: state.writingTestId,
        results: finalWritingResults,
      },
      {
        category: categories.Writing,
        testId: state.writingSecondTestId,
        results: secondResultsWriting,
      },
      {
        category: categories.Writing,
        testId: state.writingThirdTestId,
        results: thirdResultsWriting,
      },
      {
        category: categories.Math,
        testId: state.countingTestId,
        results: finalCountingResults,
      },
      {
        category: categories.Math,
        testId: state.countingSecondTestId,
        results: secondResultsCounting,
      },
      {
        category: categories.Math,
        testId: state.countingThirdTestId,
        results: thirdResultsCounting,
      },
    ],
  };
  //here is all the data that needed for funding recomendations
  return getFundingMatching(fundingMatchingRecomendations, filterQuery);
};

//function combines multiple reducers
export const reduceReducers =
  (...reducers) =>
  (state, action) =>
    reducers.reduce((acc, nextReducer) => nextReducer(acc, action), state);

const getStorageValue = (key, defaultValue) => {
  // getting stored value
  const saved = sessionStorage.getItem(key);
  const initial = JSON.parse(saved);
  return initial || defaultValue;
};

export const useSessionStorage = (key, defaultValue) => {
  const [value, setValue] = useState(() => {
    return getStorageValue(key, defaultValue);
  });

  useEffect(() => {
    // storing input name
    sessionStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
};

/* ───────── unchanged helper functions below ─────────────────── */
export const hasRequiredLevels = (state) => {
  /* … original implementation unchanged … */
  const selfAssessmentHas = (lvl) => {
    const keysSa = [
      `readingK${lvl}Results`,
      `writingK${lvl}Results`,
      `countingK${lvl}Results`,
      `reading${lvl}`,
      `writing${lvl}`,
      `counting${lvl}`,
    ];
    return keysSa.some((k) => state[k] !== undefined && state[k] !== null);
  };

  const tests = [
    { id: state.readingTestId, results: state.resultsReading, category: categories.Reading },
    { id: state.readingSecondTestId, results: state.resultsSecondReading, category: categories.Reading },
    { id: state.readingThirdTestId, results: state.resultsThirdReading, category: categories.Reading },
    { id: state.writingTestId, results: state.resultsWriting, category: categories.Writing },
    { id: state.writingSecondTestId, results: state.resultsSecondWriting, category: categories.Writing },
    { id: state.writingThirdTestId, results: state.resultsThirdWriting, category: categories.Writing },
    { id: state.countingTestId, results: state.resultsCounting, category: categories.Math },
    { id: state.countingSecondTestId, results: state.resultsSecondCounting, category: categories.Math },
    { id: state.countingThirdTestId, results: state.resultsThirdCounting, category: categories.Math },
  ];

  const testHas = (lvl) =>
    tests.some(({ id, results, category }) => {
      if (!id || !results) return false;
      return getTestFields(id, category).some(
        (field, idx) => field.competenceLevel === lvl && results[idx] !== undefined && results[idx] !== null
      );
    });

  return (selfAssessmentHas(1) || testHas(1)) && (selfAssessmentHas(3) || testHas(3));
};

export const testHasLevel = (state, domain, lvl) => {
  const tests =
    {
      Reading: [
        { id: state.readingTestId, results: state.resultsReading, category: categories.Reading },
        { id: state.readingSecondTestId, results: state.resultsSecondReading, category: categories.Reading },
        { id: state.readingThirdTestId, results: state.resultsThirdReading, category: categories.Reading },
      ],
      Writing: [
        { id: state.writingTestId, results: state.resultsWriting, category: categories.Writing },
        { id: state.writingSecondTestId, results: state.resultsSecondWriting, category: categories.Writing },
        { id: state.writingThirdTestId, results: state.resultsThirdWriting, category: categories.Writing },
      ],
      Counting: [
        { id: state.countingTestId, results: state.resultsCounting, category: categories.Math },
        { id: state.countingSecondTestId, results: state.resultsSecondCounting, category: categories.Math },
        { id: state.countingThirdTestId, results: state.resultsThirdCounting, category: categories.Math },
      ],
    }[domain] || [];

  return tests.some(({ id, results, category }) => {
    if (!id || !results) return false;
    return getTestFields(id, category).some(
      (field, idx) => field.competenceLevel === lvl && results[idx] !== undefined && results[idx] !== null
    );
  });
};
